import React, { useState, useRef, useEffect } from 'react';
import {
  Eye,
  BarChart3,
  Users,
  FileText,
  Play,
  Phone,
  Target,
  Zap,
  Shield,
  TrendingUp,
  ArrowRight,
  Check,
  Star,
  Menu,
  X,
  LogIn,
  LogOut,
  LayoutDashboard,
  Settings,
  User,
  MessageCircle,
  ChevronDown,
  Crown,
  Building2,
  Rocket,
  BookOpen
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionStatus from './SubscriptionStatus';

const Header = ({ showHamburger = false, onHamburgerClick, sectionTitle = '' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const solutionsRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();

  const handleDemoClick = () => {
    console.log('Demo clicked');
    navigate('/demo-preview');
    setIsMenuOpen(false);
  };

  const handleGetStarted = () => {
    console.log('Get Started clicked');
    navigate('/signup');
    setIsMenuOpen(false);
  };

  const handleSignIn = () => {
    console.log('Sign In clicked');
    navigate('/signin');
    setIsMenuOpen(false);
  };

  const handleSignOut = async () => {
    console.log('Sign Out clicked');
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const handleSupportClick = () => {
    console.log('Support clicked');
    window.open(`sms:9848886717`, '_blank');
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    console.log('Toggle menu clicked');
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = () => {
    console.log('Toggle dropdown clicked');
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleSolutions = () => {
    console.log('Toggle solutions clicked');
    setIsSolutionsOpen(!isSolutionsOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target)) &&
        (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) &&
        (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) &&
        (solutionsRef.current && !solutionsRef.current.contains(event.target))
      ) {
        setIsDropdownOpen(false);
        setIsMenuOpen(false);
        setIsSolutionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getInitials = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return currentUser?.email?.[0].toUpperCase() || 'U';
  };

  return (
    <div className="w-full border-b border-slate-800 py-4 px-6 bg-slate-900 relative z-50" onClick={() => console.log('Header container clicked')}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Left: logo (far left) */}
        {showHamburger ? (
          <div className="flex items-center min-w-0 flex-shrink-0">
            <button
              onClick={onHamburgerClick}
              className="mr-3 text-slate-300 hover:text-blue-400 transition-colors flex-shrink-0"
              aria-label="Toggle section menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <span className="text-lg font-bold text-blue-400 truncate">
              {sectionTitle}
            </span>
          </div>
        ) : (
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center">
              <img src="/logo192.png" alt="DeckLink Logo" className="w-5 h-5" />
            </div>
            <span className="text-xl font-light text-white">
              DeckLink<span className="text-blue-400">.co</span>
            </span>
          </a>
        )}

        {/* Center navigation for non-authenticated users */}
        {!currentUser && (
          <div className="hidden lg:flex items-center justify-center flex-1 space-x-8">
            <div className="relative" ref={solutionsRef}>
              <button
                onClick={toggleSolutions}
                className="text-slate-300 hover:text-blue-400 transition-colors text-base font-light tracking-wide flex items-center gap-2"
              >
                Solutions
                <ChevronDown className="w-4 h-4" />
              </button>
              {isSolutionsOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-slate-800 rounded-lg shadow-lg py-1 z-[60]">
                  <button
                    onClick={() => {
                      navigate('/');
                      setIsSolutionsOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <Rocket className="w-4 h-4" />
                    For Founders
                  </button>
                  <button
                    onClick={() => {
                      navigate('/for-teams');
                      setIsSolutionsOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <Building2 className="w-4 h-4" />
                    Accelerators & VC Funds
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/blog')}
              className="text-slate-300 hover:text-blue-400 transition-colors text-base font-light tracking-wide flex items-center gap-2"
            >
              <BookOpen className="w-5 h-5" />
              Blog
            </button>
            <button
              onClick={handleDemoClick}
              className="text-slate-300 hover:text-blue-400 transition-colors text-base font-light tracking-wide"
            >
              Preview Demo
            </button>
            <Link
              to="/pricing"
              className="text-slate-300 hover:text-blue-400 transition-colors text-base font-light tracking-wide"
            >
              Pricing
            </Link>
          </div>
        )}

        {/* Right: Sign In / Get Started or user menu (far right) */}
        <div className="hidden sm:flex items-center space-x-5 flex-shrink-0 ml-auto">
          {currentUser ? (
            <>
              <SubscriptionStatus />
              <button
                onClick={handleSupportClick}
                className="text-slate-300 hover:text-blue-400 transition-colors text-base font-medium tracking-wide flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Support
              </button>

              <div className="relative" ref={desktopDropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center space-x-2 focus:outline-none"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-white">{getInitials()}</span>
                  </div>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-lg py-1 z-[60]">
                    <div className="px-4 py-2 border-b border-slate-700">
                      <p className="text-sm text-slate-300 truncate">{currentUser.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { console.log('Settings clicked'); navigate('/settings'); setIsDropdownOpen(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/subscription')}
                      className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                    >
                      <Crown className="w-4 h-4" />
                      Subscription
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleSupportClick();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Support
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        await handleSignOut();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={handleSignIn}
                className="text-slate-300 hover:text-blue-400 transition-colors text-base font-light tracking-wide flex items-center gap-2"
              >
                <LogIn className="w-5 h-5" />
                Sign In
              </button>
              <button
                onClick={handleGetStarted}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-base font-medium transition-all"
              >
                Get Started
              </button>
            </>
          )}
        </div>

        {/* Mobile menu button - now shown for both signed in and signed out states */}
        <div className="flex sm:hidden items-center flex-shrink-0">
          {currentUser ? (
            <div className="relative" ref={mobileDropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{getInitials()}</span>
                </div>
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-lg py-1 z-[60]">
                  <div className="px-4 py-2 border-b border-slate-700">
                    <p className="text-sm text-slate-300 truncate">{currentUser.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      navigate('/subscription');
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    Subscription
                  </button>
                  <button
                    onClick={() => {
                      handleSupportClick();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Support
                  </button>
                  <button
                    onClick={async () => {
                      await handleSignOut();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={toggleMenu}
              className="text-slate-300 hover:text-blue-400 transition-colors"
              aria-label="Toggle menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div 
            ref={mobileMenuRef}
            className="sm:hidden fixed inset-0 bg-slate-900/50 z-[70]"
            onClick={() => setIsMenuOpen(false)}
          >
            <div 
              className="absolute right-0 top-0 h-full w-64 bg-slate-800 shadow-lg transform transition-transform duration-300 ease-in-out"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  
                    <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-violet-600 rounded-lg flex items-center justify-center">
              <img src="/logo192.png" alt="DeckLink Logo" className="w-5 h-5" />
            </div>
            <span className="text-lg font-light text-white">
              DeckLink<span className="text-blue-400">.co</span>
            </span>
          </a>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="text-slate-300 hover:text-blue-400 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {currentUser ? (
                    <>
                      <button
                        onClick={() => {
                          navigate('/dashboard');
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <LayoutDashboard className="w-5 h-5" />
                        Dashboard
                      </button>
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <Settings className="w-5 h-5" />
                        Settings
                      </button>
                      <button
                        onClick={() => {
                          handleSupportClick();
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <MessageCircle className="w-5 h-5" />
                        Support
                      </button>
                      <button
                        onClick={async () => {
                          await handleSignOut();
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <LogOut className="w-5 h-5" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <div className="text-sm text-slate-400 px-4">Solutions</div>
                        <button
                          onClick={() => {
                            navigate('/');
                            setIsMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                        >
                          <Rocket className="w-5 h-5" />
                          For Founders
                        </button>
                        <button
                          onClick={() => {
                            navigate('/for-teams');
                            setIsMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                        >
                          <Building2 className="w-5 h-5" />
                          Accelerators & VC Funds
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/blog');
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <BookOpen className="w-5 h-5" />
                        Blog
                      </button>
                      <button
                        onClick={handleDemoClick}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <Play className="w-5 h-5" />
                        Preview Demo
                      </button>
                      <Link
                        to="/pricing"
                        onClick={() => setIsMenuOpen(false)}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <Crown className="w-5 h-5" />
                        Pricing
                      </Link>
                      <button
                        onClick={handleSignIn}
                        className="w-full text-left px-4 py-3 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center gap-3"
                      >
                        <LogIn className="w-5 h-5" />
                        Sign In
                      </button>
                      <button
                        onClick={handleGetStarted}
                        className="w-full text-left px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-3"
                      >
                        Get Started
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
